import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import Business from '@mui/icons-material/Business';
import LocationOn from '@mui/icons-material/LocationOn';
import { Building, CrudPermissions } from '../../types';
import {
  getBuildings,
  addBuilding,
  updateBuilding,
  deleteBuilding,
} from '../../mock/buildings';

interface BuildingsManagementProps {
  permissions: CrudPermissions;
}

export const BuildingsManagement: React.FC<BuildingsManagementProps> = ({ permissions }) => {
  const [buildings, setBuildings] = useState<Building[]>(getBuildings);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<Building>>({
    name: '',
    code: '',
    address: '',
    isActive: true,
  });

  const refreshBuildings = () => {
    setBuildings(getBuildings());
  };

  const filteredBuildings = useMemo(() => {
    return buildings.filter((building) => {
      const matchesSearch =
        building.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        building.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (building.address && building.address.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesSearch;
    });
  }, [buildings, searchQuery]);

  const handleAdd = () => {
    setEditingBuilding(null);
    setFormData({
      name: '',
      code: '',
      address: '',
      isActive: true,
    });
    setOpenDialog(true);
  };

  const handleEdit = (building: Building) => {
    setEditingBuilding(building);
    setFormData({
      name: building.name,
      code: building.code,
      address: building.address || '',
      isActive: building.isActive,
    });
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this building? This may affect fees associated with it.')) {
      deleteBuilding(id);
      refreshBuildings();
    }
  };

  const handleSave = () => {
    if (editingBuilding) {
      updateBuilding(editingBuilding.id, {
        name: formData.name!,
        code: formData.code!,
        address: formData.address || undefined,
        isActive: formData.isActive!,
      });
    } else {
      addBuilding({
        name: formData.name!,
        code: formData.code!.toUpperCase(),
        address: formData.address || undefined,
        isActive: formData.isActive!,
      });
    }
    refreshBuildings();
    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Buildings Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage buildings and facilities
          </Typography>
        </Box>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Building
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search buildings..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(auto-fill, minmax(280px, 1fr))',
          },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {filteredBuildings.map((building) => (
          <Card
            key={building.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <Business />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, fontSize: '1rem' }}>
                    {building.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={building.code}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                    <Chip
                      label={building.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={building.isActive ? 'success' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>
              </Box>
              {building.address && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {building.address}
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
              {permissions.update && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => handleEdit(building)}
                  sx={{ flexGrow: 1, minWidth: 0 }}
                >
                  Edit
                </Button>
              )}
              {permissions.delete && (
                <IconButton
                  size="small"
                  onClick={() => handleDelete(building.id)}
                  color="error"
                  sx={{ flexShrink: 0 }}
                >
                  <Delete />
                </IconButton>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>

      {filteredBuildings.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No buildings found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add Building" to create your first building'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBuilding ? 'Edit Building' : 'Add New Building'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <TextField
              label="Building Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Main Campus"
            />
            <TextField
              label="Building Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              fullWidth
              required
              placeholder="e.g., MAIN"
              helperText="Short code used for identification (will be uppercased)"
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
              placeholder="e.g., 100 School Drive"
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.code}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
